import React from 'react';
import { IDepositAccountSortableItems } from 'services/BookingManagerApi';
import classNames from 'classnames';
import { StackedIcon } from 'ui/StackedIcon';
import CaretUpIcon from 'ui/Icons/tabler/caret-up.component.svg';
import CaretDownIcon from 'ui/Icons/tabler/caret-down.component.svg';

interface ILedgerTableProps {
  className?: string;
  children?: React.ReactNode;
}

export const LedgerTable: React.FC<ILedgerTableProps> = React.memo(({ className, children }) => (
  <>
    <div className={`${className} border border-solid border-gray-20 border-l-0 border-r-0`}>
      <table className="table table-xs table-fixed w-full border-collapse text-left font-pt-sans">{children}</table>
    </div>
  </>
));

export const renderSortIcon = (currentSortBy: string, currentSortOrder: string, newSortBy: string) => {
  const isActive = newSortBy === currentSortBy;
  const isInactive = !isActive;

  // Determine stroke colors based on sort state
  let topStrokeColor: string;
  let bottomStrokeColor: string;

  if (isInactive) {
    // Inactive state: both chevrons light grey with hover effect
    topStrokeColor = 'stroke-ivory group-hover:stroke-gray-40';
    bottomStrokeColor = 'stroke-ivory group-hover:stroke-gray-40';
  } else if (currentSortOrder === 'asc') {
    // Ascending: top chevron dark, bottom light
    topStrokeColor = 'stroke-gray-100';
    bottomStrokeColor = 'stroke-gray-40';
  } else {
    // Descending: top chevron light, bottom dark
    topStrokeColor = 'stroke-gray-40';
    bottomStrokeColor = 'stroke-gray-100';
  }

  return (
    <span className="inline-block w-20px ml-5px">
      <StackedIcon
        TopIcon={CaretUpIcon}
        BottomIcon={CaretDownIcon}
        topClassName={`w-4 h-4 fill-transparent ${topStrokeColor}`}
        bottomClassName={`w-4 h-4 fill-transparent ${bottomStrokeColor}`}
        stackDirection="vertical"
      />
    </span>
  );
};

interface IRenderHeaderSpan {
  label: string;
  currentSortBy: string;
  currentSortOrder: string;
  sortKey?: IDepositAccountSortableItems;
  labelClasses?: string;
  headerClasses?: string;
}

export const renderHeaderSpan = ({
  label,
  currentSortBy,
  currentSortOrder,
  labelClasses,
  headerClasses,
  sortKey,
}: IRenderHeaderSpan) => {
  const renderLabel = label => <span className={classNames('w-full', labelClasses ?? 'pl-2')}>{label}</span>;

  if (sortKey === undefined) {
    return <span className="group flex font-normal items-center justify-between h-8">{renderLabel(label)}</span>;
  }

  return (
    <span
      className={classNames(
        `group flex font-normal items-center justify-between h-8 hover:bg-gray-10 cursor-pointer rounded ${headerClasses}`,
        {
          'bg-gray-10': currentSortBy === sortKey,
        }
      )}
    >
      {renderLabel(label)}
      {renderSortIcon(currentSortBy, currentSortOrder, sortKey)}
    </span>
  );
};
